import {
  PROXY,
  YES_NO,
  applicantSection,
  feeInfo,
  otherAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * B3. Wniosek o wydanie warunków przyłączenia do sieci wodociągowej lub kanalizacyjnej — art. 19a ustawy z 7.06.2001 r.
 * o zbiorowym zaopatrzeniu w wodę i zbiorowym odprowadzaniu ścieków (treść wniosku: ust. 4; bez opłat: ust. 10).
 * Warunki wydaje przedsiębiorstwo wodociągowo-kanalizacyjne (gdy gmina prowadzi tę działalność sama — urząd), to nie
 * jest decyzja administracyjna: bez opłaty skarbowej, także od pełnomocnictwa (dlatego bez dowodu 17 zł).
 * Brak wzoru ogólnopolskiego; układ jak wzory pobrane 2026-10-22: GZK w Zaklikowie (sieć wodociągowa i kanalizacyjna,
 * zapotrzebowanie na wodę z podziałem na cele, przepływy, ścieki) —
 * https://gzkzaklikow.pl/wp-content/uploads/2022/02/wniosek-o-okreslenie-technicznych-warunkow-wlaczenia-do-sieci-wod-kan.pdf,
 * oraz ZWiK w Świerklańcu (przeznaczenie nieruchomości: jednorodzinny / zagrodowa / inna, ładunek zanieczyszczeń,
 * terminy poboru i zrzutu) — https://samorzad.gov.pl/attachment/a118b92a-8155-4288-89ad-3883b17677dd.
 * Oświadczenie o tytule prawnym i odpis z rejestru — praktyka przedsiębiorstw (nieobowiązkowe).
 */
const WATER: EbiCondition = { field: "siec", includes: "wodociagowa" };
const SEWAGE: EbiCondition = { field: "siec", includes: "kanalizacyjna" };

/** Pełnomocnictwo bez opłaty skarbowej — warunki przyłączenia to nie sprawa z zakresu administracji publicznej. */
const representation = representationSection();

const B3: IEbiFormDefinition = {
  code: "B3",
  version: 1,
  title: "Wniosek o wydanie warunków przyłączenia do sieci wodociągowej lub kanalizacyjnej",
  category: EEbiFormCategory.Environment,
  office: EEbiFormOffice.Municipality,
  description: "Warunki przyłączenia nieruchomości lub budynku do gminnej sieci wodociągowej albo kanalizacji sanitarnej.",
  keywords: ["przyłącze", "warunki przyłączenia", "wodociąg", "kanalizacja", "woda", "ścieki", "wod-kan"],
  organ: "Przedsiębiorstwo wodociągowo-kanalizacyjne gminy (zakład budżetowy albo spółka komunalna; urząd gminy, gdy gmina sama prowadzi tę działalność)",
  legalBasis:
    "art. 19a ustawy z dnia 7 czerwca 2001 r. o zbiorowym zaopatrzeniu w wodę i zbiorowym odprowadzaniu ścieków; regulamin dostarczania wody i odprowadzania ścieków (uchwała rady gminy)",
  request: "Wnoszę o wydanie warunków przyłączenia nieruchomości (obiektu) wskazanej poniżej do sieci wodociągowej lub kanalizacyjnej.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty (art. 19a ust. 10) — także pełnomocnictwo nie podlega opłacie skarbowej.",
  deadlineLabel: "do 21 dni",
  deadline:
    "21 dni dla budynku mieszkalnego jednorodzinnego i zabudowy zagrodowej, 45 dni w pozostałych przypadkach; w szczególnie uzasadnionych przypadkach przedłużenie o kolejne 21 albo 45 dni (art. 19a ust. 1–2).",
  sections: [
    applicantSection("Podmiot ubiegający się o przyłączenie do sieci"),
    { ...representation, fields: representation.fields.filter((field) => field.code !== "pelnomocnictwoZwolnione") },
    {
      code: "zakres",
      title: "Wniosek dotyczy przyłączenia do sieci",
      fields: [
        {
          code: "siec",
          type: "checkboxes",
          label: "Sieć",
          required: true,
          options: [
            { value: "wodociagowa", label: "wodociągowej" },
            { value: "kanalizacyjna", label: "kanalizacji sanitarnej" },
          ],
        },
      ],
    },
    {
      code: "nieruchomosc",
      title: "Nieruchomość lub obiekt do przyłączenia",
      fields: [
        { code: "adresNieruchomosci", type: "text", label: "Adres nieruchomości lub obiektu (ulica, numer, miejscowość)", required: true, maxLength: 300 },
        { code: "obreb", type: "text", label: "Obręb ewidencyjny", required: true, width: "half", maxLength: 100 },
        { code: "dzialka", type: "text", label: "Numer działki ewidencyjnej", required: true, width: "half", maxLength: 100, placeholder: "np. 125/4" },
        {
          code: "przeznaczenie",
          type: "radio",
          label: "Przeznaczenie i sposób wykorzystywania nieruchomości (obiektu)",
          required: true,
          hint: "Decyduje o terminie: 21 dni dla budynku jednorodzinnego i zabudowy zagrodowej, 45 dni w pozostałych przypadkach.",
          options: [
            { value: "jednorodzinny", label: "budynek mieszkalny jednorodzinny" },
            { value: "zagrodowa", label: "zabudowa zagrodowa" },
            { value: "inna", label: "inna zabudowa" },
          ],
        },
        {
          code: "przeznaczenieInne",
          type: "text",
          label: "Rodzaj zabudowy (np. wielorodzinna, usługowa, produkcyjna)",
          required: true,
          visibleWhen: { field: "przeznaczenie", equals: "inna" },
          maxLength: 200,
        },
        {
          code: "stanObiektu",
          type: "radio",
          label: "Obiekt",
          options: [
            { value: "istniejacy", label: "istniejący" },
            { value: "projektowany", label: "projektowany" },
            { value: "rozbudowa", label: "rozbudowa, nadbudowa albo zmiana sposobu użytkowania" },
          ],
        },
      ],
    },
    {
      code: "woda",
      title: "Zapotrzebowanie na wodę",
      visibleWhen: WATER,
      fields: [
        { code: "wodaBytowa", type: "number", label: "Średniodobowe zapotrzebowanie — cele bytowe [m³/d]", required: true, decimals: 2, min: 0, max: 100000, width: "half" },
        { code: "wodaTechnologiczna", type: "number", label: "Średniodobowe zapotrzebowanie — cele technologiczne [m³/d]", decimals: 2, min: 0, max: 100000, width: "half" },
        { code: "wodaInneCele", type: "number", label: "Średniodobowe zapotrzebowanie — inne cele [m³/d]", decimals: 2, min: 0, max: 100000, width: "half" },
        { code: "wodaMaxGodzinowy", type: "number", label: "Przepływ maksymalny godzinowy [m³/h]", required: true, decimals: 2, min: 0, max: 100000, width: "half" },
        {
          code: "wodaPpoz",
          type: "number",
          label: "Zapotrzebowanie na wodę do celów przeciwpożarowych [m³/d]",
          decimals: 2,
          min: 0,
          max: 100000,
          width: "half",
          hint: "Jeśli dotyczy.",
        },
        { code: "terminPoboru", type: "text", label: "Planowany termin rozpoczęcia poboru wody", width: "half", maxLength: 100 },
      ],
    },
    {
      code: "scieki",
      title: "Odprowadzane ścieki",
      visibleWhen: SEWAGE,
      fields: [
        { code: "sciekiBytowe", type: "number", label: "Średnia dobowa ilość ścieków bytowych [m³/d]", required: true, decimals: 2, min: 0, max: 100000, width: "half" },
        { code: "sciekiPrzemyslowe", type: "number", label: "Średnia dobowa ilość ścieków przemysłowych [m³/d]", decimals: 2, min: 0, max: 100000, width: "half" },
        {
          code: "ladunekZanieczyszczen",
          type: "text",
          label: "Wielkość ładunku zanieczyszczeń w ściekach przemysłowych",
          required: true,
          visibleWhen: { field: "sciekiPrzemyslowe", filled: true },
          maxLength: 300,
          placeholder: "np. BZT₅ … g/d",
        },
        { code: "terminZrzutu", type: "text", label: "Planowany termin rozpoczęcia dostarczania ścieków", width: "half", maxLength: 100 },
      ],
    },
    {
      code: "dodatkowe",
      title: "Informacje dodatkowe",
      fields: [
        { code: "wlasneUjecie", type: "radio", label: "Czy nieruchomość ma własne ujęcie wody?", options: YES_NO },
        {
          code: "numerRejestru",
          type: "text",
          label: "Numer w KRS lub innym rejestrze (przedsiębiorcy i osoby prawne)",
          hint: "Umożliwia przedsiębiorstwu sprawdzenie sposobu reprezentacji.",
          maxLength: 50,
        },
        feeInfo(
          "infoWarunki",
          "Wydanie warunków przyłączenia jest bezpłatne i nie jest decyzją administracyjną. Przedsiębiorstwo pisemnie potwierdzi datę złożenia wniosku; wydane warunki są ważne 2 lata (art. 19a ustawy o zbiorowym zaopatrzeniu w wodę i zbiorowym odprowadzaniu ścieków).",
        ),
      ],
    },
    replySection("Sposób odbioru warunków przyłączenia"),
  ],
  attachments: [
    {
      code: "szkic",
      label: "Plan zabudowy lub szkic sytuacyjny z usytuowaniem przyłącza",
      hint: "Usytuowanie przyłącza względem istniejącej sieci wodociągowej lub kanalizacyjnej oraz innych obiektów i sieci uzbrojenia terenu (np. na kopii mapy zasadniczej).",
      required: true,
    },
    {
      code: "tytulPrawny",
      label: "Oświadczenie o tytule prawnym do korzystania z nieruchomości",
      hint: "Nieobowiązkowo — niektóre przedsiębiorstwa o nie proszą (praktyka).",
    },
    {
      code: "odpisRejestru",
      label: "Odpis z właściwego rejestru (przedsiębiorcy)",
      hint: "Nieobowiązkowo — niektóre przedsiębiorstwa o niego proszą (praktyka).",
    },
    {
      code: "pelnomocnictwo",
      label: "Pełnomocnictwo",
      hint: "Skan podpisanego pełnomocnictwa — bez opłaty skarbowej.",
      visibleWhen: PROXY,
      required: true,
    },
    otherAttachments(),
  ],
};

export default B3;
